import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Robot, ArrowClockwise, ChatCircleDots, LinkSimple, Trash, Plus, X, Pause, Play, Lock, ShieldCheck } from "@phosphor-icons/react";
import { ClinaraLidLinks } from "@/components/ClinaraLidLinks";
import { ClinaraComplaintBank } from "@/components/ClinaraComplaintBank";


const TONE = { emerald: "bg-emerald-100 text-emerald-700", amber: "bg-amber-100 text-amber-700" };
const REASON_LABEL = {
  unauthorized: "Token webhook salah", empty: "Body kosong", non_message_event: "Bukan event pesan",
  self: "Pesan dari nomor klinik sendiri", non_personal: "Grup/status/broadcast/saluran — diabaikan",
  empty_payload: "Pesan kosong", lid_unmapped: "ID privasi (@lid) belum terpetakan ke nomor — tidak dibalas",
  lid_linked: "Kode tautan diterima — ID privasi tertaut ke nomor penguji", lid_link_invalid: "Kode tautan salah/kedaluwarsa/sudah dipakai",
  admin_manual: "Pesan manual admin — chat ditangani Admin", self_app: "Pesan keluar dari bot/notifikasi sistem — diabaikan",
  thanks_repeat: "Ucapan terima kasih beruntun — tidak dibalas lagi (10 mnt)",
  passed_auth: "Diproses", duplicate: "Pesan ganda", loop_guard: "Pola balasan bot — diabaikan",
  rate_limited: "Terlalu banyak pesan/menit", rate_limited_hour: "Batas pesan per jam tercapai (anti-spam)", paused: "Bot dijeda — tidak dibalas", takeover: "Ditangani Admin",
  not_allowed: "Bukan nomor penguji (mode uji)", autoreply_off: "Balasan otomatis mati",
  tester_gate_off: "Gerbang penguji server mati", global_gate_off: "Balasan global terkunci",
  replied_tester: "Dibalas (penguji)", replied_global: "Dibalas (global)", daily_number_limit: "Batas harian nomor",
  quota_global: "Kuota harian global habis", send_failed: "Gagal kirim",
};
const LID_SRC = { payload: "dari payload", cache: "tersimpan", lookup: "lookup gateway", link_code: "kode tautan", unmapped: "tak terpetakan" };

const Pill = ({ on, labelOn, labelOff, tone = "emerald", testid }) => (
  <span data-testid={testid} className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-bold ${on ? TONE[tone] : "bg-slate-100 text-slate-600"}`}>
    {on ? labelOn : labelOff}
  </span>
);

const Toggle = ({ checked, disabled, onChange, testid }) => (
  <button data-testid={testid} disabled={disabled} onClick={() => onChange(!checked)}
    className={`relative h-6 w-11 rounded-full transition-colors ${checked ? "bg-emerald-500" : "bg-slate-300"} ${disabled ? "cursor-not-allowed opacity-50" : ""}`}>
    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
  </button>
);

export default function Clinara() {
  const [cfg, setCfg] = useState(null);
  const [takeovers, setTakeovers] = useState([]);
  const [inbound, setInbound] = useState([]);
  const [diag, setDiag] = useState([]);
  const [newNum, setNewNum] = useState("");
  const [link, setLink] = useState("");
  const [hours, setHours] = useState(12);
  const [greet, setGreet] = useState("");
  const [confirmOn, setConfirmOn] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, t, i, wd] = await Promise.all([
        api.get("/admin/clinara/settings"),
        api.get("/admin/clinara/takeovers"),
        api.get("/admin/clinara/inbound?limit=20"),
        api.get("/admin/clinara/webhook-diag?limit=20"),
      ]);
      setCfg(s.data);
      setLink(s.data.reservation_link || "");
      setHours(s.data.admin_takeover_hours || 12);
      setGreet(s.data.greeting_text || "");
      setTakeovers(t.data);
      setInbound(i.data);
      setDiag(wd.data);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (patch, okMsg) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/clinara/config", patch);
      if (data.ok === false) {
        const map = {
          locked_by_system: "Dikunci sistem — hubungi tim teknis untuk membuka.",
          tester_required: "Tambahkan minimal 1 nomor penguji sebelum mengaktifkan balasan penguji.",
          global_locked: "Mode Uji tidak dapat dimatikan selama Balasan Global terkunci sistem.",
          invalid_hours: "Durasi ambil alih harus 1–72 jam.",
          greeting_too_long: "Teks sapaan maksimal 1000 karakter.",
        };
        toast.error(map[data.reason] || "Gagal menyimpan");
        return false;
      }
      setCfg(data);
      setLink(data.reservation_link || "");
      if (okMsg) toast.success(okMsg);
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
    finally { setBusy(false); }
  };

  const release = async (number) => {
    try { await api.post("/admin/clinara/takeover/release", { number }); toast.success("Bot diaktifkan kembali untuk nomor ini"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const addNum = async () => {
    if (!newNum.trim()) return;
    const ok = await save({ allowed_numbers: [...cfg.allowed_numbers, newNum.trim()] }, "Nomor penguji ditambahkan");
    if (ok) setNewNum("");
  };
  const removeNum = (n) => save({ allowed_numbers: cfg.allowed_numbers.filter((x) => x !== n) }, "Nomor penguji dihapus");

  const clearLogs = async () => {
    if (!window.confirm("Hapus semua log pengujian CLINARA? Tindakan ini tidak bisa dibatalkan.")) return;
    try { const { data } = await api.post("/admin/clinara/clear-logs"); toast.success(`Log dihapus (${data.deleted_inbound} pesan)`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doEnable = async () => { setConfirmOn(false); await save({ autoreply_enabled: true }, "Balasan penguji DIAKTIFKAN"); };

  if (!cfg) return <div className="p-6 text-slate-400">Memuat…</div>;
  const globalLocked = cfg.global_locked;          // WA_AUTOREPLY_ENABLED false
  const testerGate = cfg.tester_available;          // WA_AUTOREPLY_TESTER_ENABLED
  const hasTesters = (cfg.allowed_numbers || []).length > 0;
  const canEnable = testerGate && cfg.test_mode && hasTesters;   // may enable tester replies
  const enableDisabled = busy || (!cfg.autoreply_enabled && !canEnable);

  return (
    <div className="mx-auto max-w-5xl" data-testid="clinara-page">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-slate-800">
            <Robot size={26} weight="duotone" className="text-[#6A38C7]" /> CLINARA — Resepsionis Otomatis
          </h1>
          <p className="text-sm text-slate-500">Atur balasan WhatsApp otomatis klinik Anda. Perubahan langsung tersimpan.</p>
        </div>
        <button data-testid="clinara-refresh" onClick={load} className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200">
          <ArrowClockwise size={14} weight="bold" /> Muat ulang
        </button>
      </div>

      {/* Separated gate status */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div data-testid="clinara-tester-status" className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm">
          <span className="font-semibold text-slate-700">Mode Penguji</span>
          <Pill on={testerGate} labelOn="Tersedia" labelOff="Terkunci" />
        </div>
        <div data-testid="clinara-global-status" className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm">
          <span className="font-semibold text-slate-700">Balasan Global</span>
          <Pill on={!globalLocked} labelOn="Aktif tersedia" labelOff="Terkunci oleh sistem" tone="amber" />
        </div>
      </div>

      {testerGate && globalLocked && (
        <div data-testid="clinara-safe-banner" className="mb-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">
          <ShieldCheck size={18} weight="fill" /> Mode Penguji Aman — hanya nomor penguji yang dapat menerima balasan.
        </div>
      )}
      {!testerGate && globalLocked && (
        <div data-testid="clinara-locked-banner" className="mb-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <Lock size={18} weight="fill" /> Balasan otomatis <b>dinonaktifkan oleh sistem</b>. Tombol pengaktifan terkunci demi keamanan hingga tim teknis membukanya.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Kontrol utama */}
        <div className="rounded-2xl border border-rose-100/70 bg-white p-5">
          <h2 className="mb-4 text-sm font-bold text-slate-600">Kontrol Balasan Otomatis</h2>
          <div className="space-y-4 text-sm">
            <div className="flex items-center justify-between">
              <div><p className="font-semibold text-slate-700">Balasan otomatis (penguji)</p><p className="text-xs text-slate-400">Bot menjawab chat masuk hanya untuk nomor penguji</p></div>
              <div className="flex items-center gap-2">
                <Pill on={cfg.effective_tester || cfg.effective_global} labelOn="AKTIF" labelOff={cfg.paused ? "NONAKTIF — dijeda" : "NONAKTIF"} testid="clinara-autoreply-status" />
                <Toggle testid="toggle-autoreply" checked={cfg.autoreply_enabled} disabled={enableDisabled}
                  onChange={(v) => v ? (canEnable && setConfirmOn(true)) : save({ autoreply_enabled: false }, "Balasan otomatis dimatikan")} />
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-slate-100 pt-4">
              <div className="flex items-center gap-1.5">
                <div><p className="font-semibold text-slate-700">Mode uji coba</p><p className="text-xs text-slate-400">Hanya membalas nomor penguji. Nomor lain tidak dibalas.</p></div>
                {globalLocked && <Lock size={13} weight="fill" className="text-slate-400" title="Terkunci saat Balasan Global nonaktif" />}
              </div>
              <Toggle testid="toggle-testmode" checked={cfg.test_mode} disabled={busy || globalLocked}
                onChange={(v) => save({ test_mode: v }, v ? "Mode uji aktif" : "Mode uji dimatikan")} />
            </div>
            <div className="flex items-center justify-between border-t border-slate-100 pt-4">
              <div><p className="font-semibold text-slate-700">Jeda sementara semua balasan</p><p className="text-xs text-slate-400">Hentikan seketika (penguji &amp; global) tanpa mengubah pengaturan lain</p></div>
              <div className="flex items-center gap-2">
                {cfg.paused && <Pill on labelOn="DIJEDA" tone="amber" />}
                <button data-testid="toggle-pause" disabled={busy} onClick={() => save({ paused: !cfg.paused }, cfg.paused ? "Balasan dilanjutkan" : "Balasan dijeda")}
                  className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-bold ${cfg.paused ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {cfg.paused ? <><Play size={14} weight="fill" /> Lanjutkan</> : <><Pause size={14} weight="fill" /> Jeda</>}
                </button>
              </div>
            </div>
          </div>
          <div className="mt-4 space-y-1 border-t border-slate-100 pt-3 text-xs text-slate-400">
            <div className="flex items-center justify-between"><span>Koneksi WhatsApp</span><Pill on={cfg.wacloud_token_configured} labelOn="Terpasang" labelOff="Belum terpasang" /></div>
            <div className="flex items-center justify-between"><span>Kunci keamanan webhook</span><Pill on={cfg.webhook_secret_configured} labelOn="Terpasang" labelOff="Belum terpasang" /></div>
            <p className="pt-1">Untuk keamanan, semua kunci rahasia disembunyikan.</p>
          </div>
        </div>

        {/* Nomor penguji + link */}
        <div className="rounded-2xl border border-rose-100/70 bg-white p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-600">Nomor Penguji</h2>
          <div className="flex gap-2">
            <input data-testid="tester-input" value={newNum} onChange={(e) => setNewNum(e.target.value)} placeholder="cth: 08123456789"
              className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-[#6A38C7] focus:outline-none" />
            <button data-testid="tester-add" onClick={addNum} disabled={busy} className="inline-flex items-center gap-1 rounded-xl bg-[#6A38C7] px-3 py-2 text-sm font-bold text-white hover:bg-[#5a2eb0]"><Plus size={14} weight="bold" /> Tambah</button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="tester-list">
            {cfg.allowed_numbers.length === 0 ? <span className="text-xs text-slate-400">Belum ada nomor penguji.</span> :
              cfg.allowed_numbers.map((n) => (
                <span key={n} className="inline-flex items-center gap-1 rounded-full bg-[#EEE6FF] px-2.5 py-1 font-mono text-xs font-bold text-[#6A38C7]">
                  {n}<button data-testid={`tester-remove-${n}`} onClick={() => removeNum(n)} className="text-[#6A38C7]/70 hover:text-rose-600"><X size={12} weight="bold" /></button>
                </span>
              ))}
          </div>

          <h2 className="mb-2 mt-5 text-sm font-bold text-slate-600">Link Reservasi</h2>
          <div className="flex gap-2">
            <div className="flex flex-1 items-center gap-1.5 rounded-xl border border-slate-200 px-3">
              <LinkSimple size={14} className="text-slate-400" />
              <input data-testid="link-input" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…"
                className="flex-1 bg-transparent py-2 text-sm focus:outline-none" />
            </div>
            <button data-testid="link-save" onClick={() => save({ reservation_link: link }, "Link reservasi disimpan")} disabled={busy}
              className="rounded-xl bg-slate-700 px-3 py-2 text-sm font-bold text-white hover:bg-slate-800">Simpan</button>
          </div>

          <h2 className="mb-1 mt-5 text-sm font-bold text-slate-600">Teks sapaan</h2>
          <p className="mb-2 text-xs text-slate-400">Dikirim sekali per percakapan (6 jam). Tulis <code>{"{link}"}</code> untuk menyisipkan Link Reservasi. Kosongkan lalu simpan untuk kembali ke teks bawaan.</p>
          <textarea data-testid="greeting-input" value={greet} onChange={(e) => setGreet(e.target.value)} rows={4} maxLength={1000}
            className="w-full rounded-xl border border-slate-200 p-3 text-sm focus:outline-none" />
          <button data-testid="greeting-save" onClick={async () => { const ok = await save({ greeting_text: greet }, "Teks sapaan disimpan"); if (ok) load(); }} disabled={busy}
            className="mt-2 rounded-xl bg-slate-700 px-3 py-2 text-sm font-bold text-white hover:bg-slate-800">Simpan sapaan</button>

          <h2 className="mb-1 mt-5 text-sm font-bold text-slate-600">Ambil Alih Otomatis saat Admin Membalas Manual</h2>
          <p className="mb-2 text-xs text-slate-400">Bila admin membalas pasien langsung dari WA klinik, bot diam di chat itu selama durasi ini sejak pesan admin terakhir, lalu aktif lagi. Pesan dari bot & notifikasi sistem tidak memicu. Kata darurat tetap dijawab.</p>
          <div className="flex items-center gap-2">
            <input data-testid="takeover-hours-input" type="number" min={1} max={72} value={hours} onChange={(e) => setHours(e.target.value)}
              className="w-24 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none" />
            <span className="text-sm text-slate-500">jam</span>
            <button data-testid="takeover-hours-save" onClick={() => save({ admin_takeover_hours: Number(hours) }, "Durasi ambil alih disimpan")} disabled={busy}
              className="rounded-xl bg-slate-700 px-3 py-2 text-sm font-bold text-white hover:bg-slate-800">Simpan</button>
          </div>
        </div>
      </div>

      {/* Tautan @lid */}
      {cfg && <ClinaraLidLinks testers={(cfg.allowed_numbers || []).join(",")} />}

      {/* Bank jawaban keluhan */}
      <ClinaraComplaintBank />

      {/* Takeover */}
      <div className="mt-4 rounded-2xl border border-rose-100/70 bg-white p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-600">Percakapan Diambil Alih Admin ({takeovers.length})</h2>
        {takeovers.length === 0 ? <p className="text-sm text-slate-400">Tidak ada percakapan yang sedang ditangani admin.</p> : (
          <ul className="space-y-2">
            {takeovers.map((t) => (
              <li key={t.number} data-testid={`takeover-${t.number}`} className="flex items-center justify-between rounded-xl bg-amber-50 px-3 py-2">
                <div><p className="font-mono text-sm font-bold text-slate-700">{t.number_masked}</p><p data-testid={`takeover-info-${t.number}`} className="text-[11px] text-slate-400">{t.reason === "admin_manual" ? "Admin membalas manual" : t.last_admin_at ? "Dialihkan bot · admin sudah membalas" : "Dialihkan bot"} · {String(t.last_admin_at || t.created_at || "").slice(0, 16).replace("T", " ")}{t.auto_until ? ` · bot aktif lagi ${new Date(t.auto_until).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" })}` : ""}</p></div>
                <button data-testid={`reactivate-${t.number}`} onClick={() => release(t.number)} className="rounded-lg bg-[#6A38C7] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#5a2eb0]">Aktifkan bot</button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Log */}
      <div className="mt-4 rounded-2xl border border-rose-100/70 bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-1.5 text-sm font-bold text-slate-600"><ChatCircleDots size={16} /> Log Pesan Masuk (termasking)</h2>
          <button data-testid="clear-logs" onClick={clearLogs} className="inline-flex items-center gap-1 rounded-lg bg-rose-100 px-3 py-1.5 text-xs font-bold text-rose-700 hover:bg-rose-200"><Trash size={14} weight="bold" /> Hapus log pengujian</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-100 text-xs text-slate-500">
              <tr><th className="px-2 py-2">Waktu</th><th className="px-2 py-2">Nomor</th><th className="px-2 py-2">Maksud</th><th className="px-2 py-2">Alasan</th><th className="px-2 py-2 text-center">Penguji</th><th className="px-2 py-2 text-center">Dibalas</th></tr>
            </thead>
            <tbody>
              {inbound.length === 0 && <tr><td colSpan={6} className="px-2 py-4 text-center text-slate-400">Belum ada pesan masuk.</td></tr>}
              {inbound.map((d) => (
                <tr key={d.id} className="border-b border-slate-50 last:border-0">
                  <td className="px-2 py-2 text-xs text-slate-400">{String(d.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-2 py-2 font-mono text-xs text-slate-600">{d.from_masked}</td>
                  <td className="px-2 py-2 text-slate-600">{d.intent}</td>
                  <td className="px-2 py-2 text-xs text-slate-500" title={d.reason || ""}>{REASON_LABEL[d.reason] || d.reason}{d.from_kind === "lid" ? " · via ID privasi" : ""}</td>
                  <td className="px-2 py-2 text-center">{d.allowed ? "✓" : "–"}</td>
                  <td className="px-2 py-2 text-center">{d.replied ? "✓" : "–"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Diagnostik webhook (zero-PII) */}
      <div className="mt-4 rounded-2xl border border-rose-100/70 bg-white p-5" data-testid="clinara-webhook-diag">
        <h2 className="mb-1 flex items-center gap-1.5 text-sm font-bold text-slate-600"><ShieldCheck size={16} /> Diagnostik Webhook (20 terakhir)</h2>
        <p className="mb-3 text-xs text-slate-400">Hanya metadata teknis. Tidak menyimpan isi pesan, token, query, atau ID privasi; nomor hanya tampil termasking setelah ID privasi (@lid) dipetakan.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-100 text-xs text-slate-500">
              <tr>
                <th className="px-2 py-2">Waktu</th>
                <th className="px-2 py-2 text-center">Masuk</th>
                <th className="px-2 py-2 text-center">Token ada</th>
                <th className="px-2 py-2 text-center">Token cocok</th>
                <th className="px-2 py-2">Content-Type</th>
                <th className="px-2 py-2">Event</th>
                <th className="px-2 py-2">Keys</th>
                <th className="px-2 py-2">Jenis pengirim</th>
                <th className="px-2 py-2">Nomor terpetakan (termasking)</th>
                <th className="px-2 py-2">Alasan</th>
              </tr>
            </thead>
            <tbody>
              {diag.length === 0 && <tr><td colSpan={10} className="px-2 py-4 text-center text-slate-400">Belum ada diagnostik webhook.</td></tr>}
              {diag.map((d) => (
                <tr key={d.id} data-testid={`diag-row-${d.id}`} className="border-b border-slate-50 last:border-0">
                  <td className="px-2 py-2 text-xs text-slate-400">{String(d.created_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="px-2 py-2 text-center">{d.request_masuk ? "✓" : "–"}</td>
                  <td className="px-2 py-2 text-center">{d.token_ada ? "✓" : "–"}</td>
                  <td className="px-2 py-2 text-center">{d.token_cocok ? <span className="text-emerald-600">✓</span> : <span className="text-rose-500">✕</span>}</td>
                  <td className="px-2 py-2 text-xs text-slate-500">{d.content_type || "–"}</td>
                  <td className="px-2 py-2 text-xs text-slate-600">{d.event || "–"}</td>
                  <td className="px-2 py-2 font-mono text-[11px] text-slate-500">{(d.top_keys || []).join(", ") || "–"}</td>
                  <td data-testid={`diag-from-kind-${d.id}`} className="px-2 py-2 font-mono text-[11px] text-slate-600">{d.from_kind || "–"}</td>
                  <td data-testid={`diag-mapped-${d.id}`} className="px-2 py-2 font-mono text-[11px] text-slate-600">{d.mapped_masked ? `${d.mapped_masked}${d.lid_source ? ` (${LID_SRC[d.lid_source] || d.lid_source})` : ""}` : "–"}</td>
                  <td data-testid={`diag-reason-${d.id}`} className="px-2 py-2 text-xs text-slate-500" title={d.skip_reason || ""}>{REASON_LABEL[d.skip_reason] || d.skip_reason || "–"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirm activate dialog */}
      {confirmOn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="confirm-enable-dialog">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="font-heading text-lg font-bold text-slate-800">Aktifkan balasan penguji?</h3>
            <p className="mt-2 text-sm text-slate-500">Bot akan membalas chat masuk <b>hanya untuk nomor penguji</b> (Balasan Global tetap terkunci sistem). Pastikan nomor penguji sudah benar.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirmOn(false)} className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-bold text-slate-600">Batal</button>
              <button data-testid="confirm-enable-yes" onClick={doEnable} className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700">Ya, aktifkan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
